import {defineStore} from 'pinia'
import {reactive, toRefs, computed, watch, ref} from 'vue'
import {useRoute, useRouter} from 'vue-router'
import {useStorage} from '@vueuse/core'
import {useStore} from 'shared/stores/store'
import {useSessionStore} from 'shared/stores/session'
import {useRouterHelpers} from 'shared/composables/useRouterHelpers'
import {useWindowSize} from 'shared/composables/useWindowSize'
import analytics from 'shared/utils/analytics'

const MIN_SIZE = '240'

const initialLayoutState = {
	devToolsHidden: false,
	detailsHidden: false,
	devToolsPane: 'console',
	mobileTab: 'media',
}
Object.freeze(initialLayoutState)

export const useSessionLayoutStore = defineStore('sessionLayout', () => {
	const state = reactive({
		...initialLayoutState,
	})
	const minSize = MIN_SIZE
	const gridColumnTemplates = {
		'open-open-open': ['1fr', '1.1fr', `${MIN_SIZE}px`],
		'open-open-closed': ['1fr', '1fr'],
		'open-closed-open': ['1fr', `${MIN_SIZE}px`],
		'open-closed-closed': ['1fr'],
		mobile: ['1fr'],
	}
	const gutterWidth = '2px'
	const localStorageKey = 'sessionLayoutColumns'
	const initialQueryParameters = ref({})

	const route = useRoute()
	const router = useRouter()
	const store = useStore()
	const sessionStore = useSessionStore()
	const routerHelpers = useRouterHelpers(router)
	const {isMobile} = useWindowSize()

	const devToolsPanes = computed(() => {
		let devToolsPanes = {}
		if (sessionStore.sessionUploadMethod === 'app-live') {
			devToolsPanes = {
				console: 'AppLogs',
				system: 'System Info',
			}
		} else {
			devToolsPanes = {
				console: 'Console',
				network: 'Network',
				system: 'System Info',
				test: 'E2E Helper',
			}
		}
		return devToolsPanes
	})

	const sessionIdFromUrl = computed(() => route.params.sessionId || route.query.sessionId)

	const isMediaPanelMaximized = computed(() => state.devToolsHidden && state.detailsHidden)

	const gridState = computed(() => {
		if (isMobile.value) return 'mobile'
		const status = ['open']
		state.devToolsHidden ? status.push('closed') : status.push('open')
		state.detailsHidden ? status.push('closed') : status.push('open')
		return status.join('-')
	})
	const columns = computed(() => gridColumnTemplates[gridState.value])

	const nonDefaultLayoutState = computed(() => {
		// Compute difference between the current view state and the default view state;
		// In the browser extension, any difference from our default layout is considered non-default;
		// In the web app, only differences from our default layout that also diverge from the layout persisted with the session are considered a non-default layout state
		const comparedLayoutState = {
			...initialLayoutState,
			...(!store.isExtension && sessionStore.session?.layout),
		}
		const changedKeys = Object.keys(initialLayoutState).filter(
			key => state[key] !== comparedLayoutState[key] || initialQueryParameters.value[key]
		)
		const nonDefaultLayoutState = Object.fromEntries(changedKeys.map(key => [key, state[key]]))
		return nonDefaultLayoutState
	})

	watch(isMobile, () => {
		recomputePanelStates()
	})

	watch(
		sessionIdFromUrl,
		(sessionId, previousSessionId) => {
			// Reset session layout query params in the session URL when leaving session details
			if (!sessionId && previousSessionId) {
				initialQueryParameters.value = {}
				const queryParams = {}
				Object.entries(initialLayoutState).forEach(([key, value]) => {
					state[key] = value
					queryParams[key] = null
				})
				return routerHelpers?.replaceQueryParams(queryParams)
			}
			if (sessionId) initialQueryParameters.value = route.query
			Object.entries(route.query).forEach(([key, value]) => {
				if (state[key]) state[key] = value
			})

			recomputePanelStates()
		},
		{immediate: true}
	)

	watch(
		() => sessionStore.session?.layout,
		sessionLayout => {
			if (!sessionLayout) return
			Object.entries(sessionLayout).forEach(([key, value]) => {
				if (initialQueryParameters.value[key]) return
				state[key] = value
			})
		},
		{
			immediate: true,
			flush: 'sync',
		}
	)

	watch(nonDefaultLayoutState, (layoutState, previousLayoutState) => {
		if (!sessionIdFromUrl.value) return
		if (JSON.stringify(layoutState) === JSON.stringify(previousLayoutState)) return

		if (store.isExtension) {
			// In the browser extension, every new session starts with the default layout,
			// and dev tools are shown depending on whether "tech mode" is enabled or not.
			// The layout is still persisted in session storage, so that reloading a local session
			// (while developing) maintains the user-preferred state.
			if (sessionStorage) sessionStorage.sessionLayout = JSON.stringify(layoutState)
		} else {
			// In the web app, update URL to reflect layout state that diverges from the session's / general default layout state.
			const queryParams = {}
			Object.keys(initialLayoutState).forEach(key => {
				// Override existing layout query parameters that were non-default but now are default with `null`, which removes them from the URL.
				if (route.query[key] && !Object.hasOwn(layoutState, key)) queryParams[key] = null
				if (Object.hasOwn(layoutState, key)) queryParams[key] = String(layoutState[key])
			})
			routerHelpers.replaceQueryParams(queryParams)
		}
	})

	function recomputePanelStates() {
		const sessionLayout = sessionStore.session?.layout
		state.devToolsHidden = computePanelState({
			queryParam: 'devToolsHidden',
			sessionLayout: sessionLayout?.devToolsHidden,
		})
		state.detailsHidden = computePanelState({
			queryParam: 'detailsHidden',
			sessionLayout: sessionLayout?.detailsHidden,
		})
	}

	function computePanelState({queryParam, sessionLayout}) {
		if (route.query[queryParam]) return route.query[queryParam] === 'true'
		return Boolean(sessionLayout)
	}

	function trackSessionLayoutChange(from, changed) {
		analytics.track('Session Layout Changed', {
			from,
			changed,
			details: !state.detailsHidden,
			devTools: !state.devToolsHidden,
		})
	}

	function toggleDevToolsPanel({from}) {
		state.devToolsHidden = !state.devToolsHidden
		trackSessionLayoutChange(from, ['devTools'])
	}

	function toggleDetailsPanel({from}) {
		state.detailsHidden = !state.detailsHidden
		trackSessionLayoutChange(from, ['details'])
	}

	function minimizeMediaPanel() {
		state.devToolsHidden = false
		state.detailsHidden = false
		trackSessionLayoutChange('media', ['details', 'devTools'])
	}

	function expandMediaPanel() {
		state.devToolsHidden = true
		state.detailsHidden = true
		trackSessionLayoutChange('media', ['details', 'devTools'])
	}

	function openDevToolsPane(pane) {
		state.devToolsPane = pane
		analytics.track(`Session ${devToolsPanes[pane]} Button Clicked`)
	}

	/**
	 * SESSION DEV TOOLS OPTIONS
	 */
	// for backwards compatibility, we also check for wrapConsoleText which was previously set as 'wrap' or 'unwrap
	// can be removed after release v1.87.0
	const wrapConsoleText = useStorage('wrapConsoleText')
	const wrapDevToolsText = useStorage('wrap-dev-tools-text', false || Boolean(wrapConsoleText.value))

	const scrollDevToolsWithPlayback = useStorage('scroll-dev-tools-with-playback', true)

	return {
		...toRefs(state),
		minSize,
		gridColumnTemplates,
		devToolsPanes,
		initialLayoutState,
		gutterWidth,
		localStorageKey,
		sessionIdFromUrl,
		isMediaPanelMaximized,
		gridState,
		columns,
		nonDefaultLayoutState,
		trackSessionLayoutChange,
		toggleDevToolsPanel,
		toggleDetailsPanel,
		minimizeMediaPanel,
		expandMediaPanel,
		openDevToolsPane,
		// dev tools options
		wrapDevToolsText,
		scrollDevToolsWithPlayback,
	}
})
